import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Heading, Badge, AccordionItem, AccordionTrigger, Accordion, AccordionContent, Alert, Spinner } from "@aidkitorg/component-library";
import type { DeltaNotificationResult, DeltaFollowUpResult, DeltaNotificationsWorkflowResult } from "@aidkitorg/aidkit/lib/distro/review/workflows/deltaNotificationsWorkflow";
import type { NotificationResult, NotificationsWorkflowResult } from "@aidkitorg/aidkit/lib/distro/review/workflows/notificationsWorkflow";
import type { TargetFieldsWorkflowResult } from "@aidkitorg/aidkit/lib/distro/review/workflows/targetFieldsWorkflow";
import type { DeltaTargetFieldsWorkflowResult } from "@aidkitorg/aidkit/lib/distro/review/workflows/deltaTargetFieldsWorkflow";
import { usePost } from "../API";
import { ansiToReact } from "./ansiToReact";

const POLLING_INTERVAL = 5000;

type WorkflowStatus = 'pending' | 'scheduled' | 'in_progress' | 'completed' | 'failed' | 'cancelled';

type WorkflowState<T> = {
  status: WorkflowStatus;
  result: T | undefined;
} | null;

type ChangedDependency = {
  field: string;
  type: string[];
  changeCount: number;
  topChange: { before: string | null; after: string | null; count: number } | undefined;
};

// Shared target fields field shape between TargetFieldsWorkflowResult and DeltaTargetFieldsWorkflowResult
type TargetFieldEntry = {
  key: string;
  countChangedApplicants: number;
  beforeAfterValues: { before: string | null; after: string | null; count: number }[];
};

function buildTargetFieldsMap(fields: TargetFieldEntry[]): Record<string, TargetFieldEntry> {
  return fields.reduce((acc, field) => {
    acc[field.key] = field;
    return acc;
  }, {} as Record<string, TargetFieldEntry>);
}

function computeChangedDependencies(
  dependencies: DeltaNotificationResult['dependencies'],
  targetFieldsMap: Record<string, TargetFieldEntry>
): ChangedDependency[] {
  if (!dependencies || dependencies.length === 0) return [];
  return dependencies.map(({ field, type }) => {
    const fieldData = targetFieldsMap[field];
    const changeCount = fieldData?.countChangedApplicants || 0;
    const topChange = fieldData?.beforeAfterValues?.[0];
    return { field, type, changeCount, topChange };
  }).filter(({ changeCount }) => changeCount > 0);
}

// Full notifications workflow result keyed by targetPrefix
type FullNotifMap = Record<string, NotificationResult>;

function buildFullNotifMap(result: NotificationsWorkflowResult): FullNotifMap {
  const map: FullNotifMap = {};
  for (const r of result.notificationResults) {
    const key = r.newNotification?.targetPrefix || r.oldNotification?.targetPrefix;
    if (key) map[key] = r;
  }
  return map;
}

// ─── NotificationCard ────────────────────────────────────────────────────────

type NotificationCardProps = {
  result: DeltaNotificationResult | DeltaFollowUpResult;
  type: DeltaNotificationResult['changeType'];
  index: number;
  targetFieldsMap: Record<string, TargetFieldEntry>;
  /** Matching full-workflow result for this notification (undefined = not yet available) */
  fullResult?: NotificationResult | undefined;
};

function NotificationCard({ result, type, index, targetFieldsMap, fullResult }: NotificationCardProps) {
  const notification = result.newNotification || result.oldNotification;
  if (!notification) return null;

  const target = 'targetPrefix' in notification ? notification.targetPrefix : notification.suffix;
  const isFollowUp = 'suffix' in notification;

  const changedDependencies = useMemo(
    () => computeChangedDependencies(result.dependencies, targetFieldsMap),
    [result.dependencies, targetFieldsMap]
  );

  const getBorderClass = () => {
    switch (type) {
      case 'new': return 'rounded-2xl border-2 border-green-500';
      case 'removed': return 'rounded-2xl border-2 border-red-500';
      case 'changed': return 'rounded-2xl border-2 border-yellow-500';
      case 'unchanged': return 'rounded-2xl border-gray-300';
      default: return '';
    }
  };

  const getBadgeVariant = (): 'success' | 'error' | 'warning' | 'neutral' => {
    switch (type) {
      case 'new': return 'success';
      case 'removed': return 'error';
      case 'changed': return 'warning';
      default: return 'neutral';
    }
  };

  const renderEnabledBadge = (enabled: 'enabled' | 'disabled' | null) => {
    if (!enabled) return null;
    return (
      <Badge variant={enabled === 'enabled' ? 'success' : 'error'} className="text-xs">
        {enabled === 'enabled' ? 'Enabled' : 'Disabled'}
      </Badge>
    );
  };

  const renderEnabledBadges = () => {
    if (
      type === 'changed' &&
      result.oldEnabled && result.newEnabled &&
      result.oldEnabled !== result.newEnabled
    ) {
      return <>{renderEnabledBadge(result.oldEnabled)} → {renderEnabledBadge(result.newEnabled)}</>;
    }
    return renderEnabledBadge(type === 'removed' ? result.oldEnabled : result.newEnabled);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const renderMethodRow = (
    method: 'email' | 'sms' | 'whatsapp',
    oldResult: { count?: number; error?: string; query?: string } | undefined,
    newResult: { count?: number; error?: string; query?: string } | undefined,
    showComparison: boolean
  ) => {
    const oldCount = oldResult?.count;
    const newCount = newResult?.count;
    const diff = showComparison && oldCount !== newCount
      ? ((newResult?.count || 0) - (oldResult?.count || 0) > 0
        ? `+${(newResult?.count || 0) - (oldResult?.count || 0)}`
        : `${(newResult?.count || 0) - (oldResult?.count || 0)}`)
      : null;
    const isIncrease = (newCount || 0) > (oldCount || 0);
    const hasChanged = oldCount !== newCount;
    const error = newResult?.error || oldResult?.error;
    const query = newResult?.query;

    return (
      <div key={method} className="border-b border-gray-100 last:border-b-0 py-2">
        <div className="flex items-center gap-2">
          <span className="w-20 font-medium">{method}:</span>
          {error ? (
            <span className="text-red-600 text-xs">{error}</span>
          ) : (
            <>
              <span>
                {showComparison ? (
                  <>{oldCount ?? 0}{hasChanged ? ` → ${newCount ?? 0}` : ''} eligible</>
                ) : (
                  <>{newCount ?? oldCount ?? 0} eligible</>
                )}
              </span>
              {diff && (
                <span className={`font-medium ${isIncrease ? 'text-green-600' : 'text-red-600'}`}>
                  ({diff})
                </span>
              )}
            </>
          )}
          {query && (
            <button
              onClick={() => copyToClipboard(query)}
              className="ml-auto text-xs text-blue-600 hover:text-blue-800 hover:underline"
              title="Copy SQL query to clipboard"
            >
              Copy SQL
            </button>
          )}
        </div>
        {query && (
          <Accordion type="single" className="mt-1">
            <AccordionItem value="query" className="border-0">
              <AccordionTrigger className="py-1 text-xs text-gray-500 hover:no-underline">
                Show query
              </AccordionTrigger>
              <AccordionContent>
                <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto max-h-40 overflow-y-auto">
                  {query}
                </pre>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </div>
    );
  };

  const renderEligibilityCounts = () => {
    if (!fullResult) return null;

    const newByMethod = fullResult.newSurveyResult && 'byMethod' in fullResult.newSurveyResult
      ? fullResult.newSurveyResult.byMethod
      : undefined;
    const oldByMethod = fullResult.oldSurveyResult && 'byMethod' in fullResult.oldSurveyResult
      ? fullResult.oldSurveyResult.byMethod
      : undefined;

    const allMethods = (['email', 'sms', 'whatsapp'] as const).filter(
      method => (newByMethod && method in newByMethod) || (oldByMethod && method in oldByMethod)
    );

    if (allMethods.length === 0) return null;

    const showComparison = type === 'changed' || type === 'unchanged';

    return (
      <div className="text-sm text-gray-600 mt-2">
        {fullResult.newSurveyResult && 'error' in fullResult.newSurveyResult ? (
          <span className="text-red-600 text-xs">Error: {fullResult.newSurveyResult.error}</span>
        ) : (
          <div>
            {isFollowUp && notification && 'after' in notification && (
              <div className="text-gray-500 mb-2">
                after {notification.after.amount} {notification.after.unit}
              </div>
            )}
            {allMethods.map(method =>
              renderMethodRow(method, oldByMethod?.[method], newByMethod?.[method], showComparison)
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDependencies = () => {
    if (changedDependencies.length === 0) return null;
    if (type === 'removed') return null;

    return (
      <div className="mt-3">
        <div className="overflow-x-auto">
          <table className="w-auto text-xs">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 pr-4 font-medium text-gray-600">Target Field</th>
                <th className="text-left py-2 pr-4 font-medium text-gray-600">Location</th>
                <th className="text-right py-2 pr-4 font-medium text-gray-600">Affected Applicants</th>
                <th className="text-left py-2 font-medium text-gray-600">Top Change</th>
              </tr>
            </thead>
            <tbody>
              {changedDependencies.map(({ field, type: depType, changeCount, topChange }) => (
                <tr key={field} className="border-b border-gray-100">
                  <td className="py-2 pr-4">
                    <Badge variant={depType.includes('content') ? 'info' : 'neutral'} className="text-xs">
                      {field}
                    </Badge>
                  </td>
                  <td className="py-2 pr-4 text-gray-600">{depType.join(', ')}</td>
                  <td className="py-2 pr-4 text-center">
                    <span className="font-medium text-orange-600">{changeCount.toLocaleString()}</span>
                  </td>
                  <td className="py-2">
                    {topChange ? (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-mono px-1 py-0.5 bg-orange-100 border border-orange-200 rounded text-orange-800">
                          {topChange.before ?? 'null'}
                        </span>
                        <span className="text-gray-400">→</span>
                        <span className="font-mono px-1 py-0.5 bg-blue-100 border border-blue-200 rounded text-blue-800">
                          {topChange.after ?? 'null'}
                        </span>
                        <span className="text-gray-500">({topChange.count})</span>
                      </div>
                    ) : (
                      <span className="text-gray-400">No changes</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Match followup full results by suffix
  const getFollowUpFullResult = (suffix: string): NotificationResult | undefined => {
    if (!fullResult) return undefined;
    const followup = fullResult.followups.find(
      f => (f.newNotification?.suffix || f.oldNotification?.suffix) === suffix
    );
    if (!followup) return undefined;
    // Coerce FollowUpResult to NotificationResult shape (compatible subset) for renderEligibilityCounts
    return {
      newNotification: null,
      oldNotification: null,
      diff: undefined,
      newSurveyResult: followup.newSurveyResult,
      oldSurveyResult: followup.oldSurveyResult,
      changeType: followup.changeType,
      followups: [],
    } as unknown as NotificationResult;
  };

  return (
    <Card key={index} className={getBorderClass()}>
      <CardBody>
        <div className="font-medium flex items-center gap-2 mb-2">
          <Heading level={isFollowUp ? "h6" : "h4"} size="h4" className="mb-1">
            {'name' in notification && notification.name && (
              <span className="pr-2">{notification.name}</span>
            )} ({target})
          </Heading>
          {isFollowUp && 'after' in notification && (
            <div className="text-gray-500 text-sm">
              after {notification.after.amount} {notification.after.unit}
            </div>
          )}
          {renderEnabledBadges()}
          <span className="flex w-full justify-end">
            <Badge variant={getBadgeVariant()} className="text-xs">{type}</Badge>
          </span>
        </div>

        {renderEligibilityCounts()}
        {renderDependencies()}

        {'followups' in result && result.followups.length > 0 && (
          <div className="mt-3">
            <Heading level="h5" className="mb-1">Follow ups</Heading>
            <div className="flex flex-col gap-2">
              {result.followups.map((followUp) => {
                const suffix = followUp.newNotification?.suffix || followUp.oldNotification?.suffix;
                return (
                  <NotificationCard
                    key={suffix}
                    result={followUp}
                    type={followUp.changeType}
                    index={index}
                    targetFieldsMap={targetFieldsMap}
                    fullResult={suffix ? getFollowUpFullResult(suffix) : undefined}
                  />
                );
              })}
            </div>
          </div>
        )}

        {result.diff && (
          <Accordion type="single">
            <AccordionItem value="diff">
              <AccordionTrigger>
                <span className="text-xs text-gray-500">Show changes</span>
              </AccordionTrigger>
              <AccordionContent>
                <pre className="mt-3 border border-gray-200 bg-gray-50 rounded-md p-2 overflow-x-auto">
                  {ansiToReact(result.diff).segments.map((segment, i) => (
                    <React.Fragment key={i}>{segment}</React.Fragment>
                  ))}
                </pre>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </CardBody>
    </Card>
  );
}

// ─── WorkflowProgressTracker ─────────────────────────────────────────────────

type StepDef = {
  id: number;
  name: string;
  workflowStatus: WorkflowStatus | null;
};

function WorkflowProgressTracker({ steps }: { steps: StepDef[] }) {
  const failedSteps = steps.filter(
    s => s.workflowStatus === 'failed' || s.workflowStatus === 'cancelled'
  );

  // Index of the first non-completed step; -1 once everything has completed
  const firstIncompleteIndex = steps.findIndex(s => s.workflowStatus !== 'completed');
  const isComplete = firstIncompleteIndex === -1;

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        {isComplete ? (
          <>
            <Badge variant="success" className="text-xs">✓</Badge>
            <span className="font-medium text-gray-700">Analysis complete!</span>
          </>
        ) : (
          <>
            <Spinner size="xsmall" />
            <span>
              Still analysing... (Step {firstIncompleteIndex + 1}/{steps.length}: {steps[firstIncompleteIndex].name})
            </span>
          </>
        )}
      </div>
      {failedSteps.map(step => (
        <Alert key={step.id} variant="danger" title={`${step.name} failed`} className="mt-3">
          The {step.name} workflow {step.workflowStatus === 'cancelled' ? 'was cancelled due to a dependency failing' : 'failed to complete'}.
          Some notification details may be unavailable.
        </Alert>
      ))}
    </div>
  );
}

// ─── NotificationsWorkflowReview ─────────────────────────────────────────────

export default function NotificationsWorkflowReview({
  distroCommitId,
}: {
  distroCommitId: string;
}) {
  const getWorkflow = usePost('/distro-review/workflow');

  const [deltaNotifWorkflow, setDeltaNotifWorkflow] =
    useState<WorkflowState<DeltaNotificationsWorkflowResult>>(null);
  const [targetFieldsWorkflow, setTargetFieldsWorkflow] =
    useState<WorkflowState<TargetFieldsWorkflowResult | DeltaTargetFieldsWorkflowResult>>(null);
  const [fullNotifWorkflow, setFullNotifWorkflow] =
    useState<WorkflowState<NotificationsWorkflowResult>>(null);

  // Poll delta_notifications (always; no deps, fastest workflow)
  useEffect(() => {
    if (!distroCommitId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await getWorkflow({ workflowName: 'delta_notifications', distroCommitId });
        if (!cancelled && !res.error && res.workflow) {
          setDeltaNotifWorkflow({
            status: res.workflow.status as WorkflowStatus,
            result: res.workflow.result as DeltaNotificationsWorkflowResult | undefined,
          });
        }
      } catch (e) {
        console.error('Error loading delta_notifications workflow:', e);
      }
    };
    load();
    const interval = setInterval(() => {
      setDeltaNotifWorkflow(prev => {
        if (!prev || ['in_progress', 'pending', 'scheduled'].includes(prev.status)) load();
        else clearInterval(interval);
        return prev;
      });
    }, POLLING_INTERVAL);
    return () => { cancelled = true; clearInterval(interval); };
  }, [distroCommitId]);

  // Poll delta_target_fields for the dependency map below -- it's fast and always available,
  // and this only needs to know which fields would change, not the full sampled comparison.
  useEffect(() => {
    if (!distroCommitId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await getWorkflow({ workflowName: 'delta_target_fields', distroCommitId });
        if (!cancelled && !res.error && res.workflow) {
          setTargetFieldsWorkflow({
            status: res.workflow.status as WorkflowStatus,
            result: res.workflow.result as TargetFieldsWorkflowResult | DeltaTargetFieldsWorkflowResult | undefined,
          });
        }
      } catch (e) {
        console.error('Error loading delta_target_fields workflow:', e);
      }
    };
    load();
    const interval = setInterval(() => {
      setTargetFieldsWorkflow(prev => {
        if (!prev || ['in_progress', 'pending', 'scheduled'].includes(prev.status)) load();
        else clearInterval(interval);
        return prev;
      });
    }, POLLING_INTERVAL);
    return () => { cancelled = true; clearInterval(interval); };
  }, [distroCommitId]);

  // Poll full notifications workflow (recipient counts)
  useEffect(() => {
    if (!distroCommitId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await getWorkflow({ workflowName: 'notifications', distroCommitId });
        if (!cancelled && !res.error && res.workflow) {
          setFullNotifWorkflow({
            status: res.workflow.status as WorkflowStatus,
            result: res.workflow.result as NotificationsWorkflowResult | undefined,
          });
        }
      } catch (e) {
        console.error('Error loading notifications workflow:', e);
      }
    };
    load();
    const interval = setInterval(() => {
      setFullNotifWorkflow(prev => {
        if (!prev || ['in_progress', 'pending', 'scheduled'].includes(prev.status)) load();
        else clearInterval(interval);
        return prev;
      });
    }, POLLING_INTERVAL);
    return () => { cancelled = true; clearInterval(interval); };
  }, [distroCommitId]);

  const targetFieldsMap = useMemo<Record<string, TargetFieldEntry>>(() => {
    const fields = targetFieldsWorkflow?.result?.success
      ? (targetFieldsWorkflow.result as { targetFields: TargetFieldEntry[] }).targetFields
      : [];
    return buildTargetFieldsMap(fields || []);
  }, [targetFieldsWorkflow]);

  const fullNotifMap = useMemo<FullNotifMap>(() => {
    if (!fullNotifWorkflow?.result?.success) return {};
    return buildFullNotifMap(fullNotifWorkflow.result);
  }, [fullNotifWorkflow]);

  // Build stepper step definitions
  const stepperSteps = useMemo<StepDef[]>(() => [
    {
      id: 0,
      name: 'Notification Diffs',
      workflowStatus: (deltaNotifWorkflow?.status as WorkflowStatus | undefined) ?? null,
    },
    {
      id: 1,
      name: 'Target Fields',
      workflowStatus: (targetFieldsWorkflow?.status as WorkflowStatus | undefined) ?? null,
    },
    {
      id: 2,
      name: 'Recipient Counts',
      workflowStatus: (fullNotifWorkflow?.status as WorkflowStatus | undefined) ?? null,
    },
  ], [deltaNotifWorkflow?.status, targetFieldsWorkflow?.status, fullNotifWorkflow?.status]);

  const renderContent = () => {
    if (!deltaNotifWorkflow) {
      return <Spinner className="p-8" />;
    }

    if (['pending', 'scheduled', 'in_progress'].includes(deltaNotifWorkflow.status)) {
      return (
        <Alert title="Processing" variant="primary">
          Notifications analysis is in progress. Results will appear here when complete.
        </Alert>
      );
    }

    if (deltaNotifWorkflow.status === 'failed') {
      return (
        <Alert title="Workflow Failed" variant="danger">
          The notifications workflow failed to complete.
        </Alert>
      );
    }

    if (!deltaNotifWorkflow.result?.success || !deltaNotifWorkflow.result.notificationResults) {
      return null;
    }

    const results = deltaNotifWorkflow.result.notificationResults;

    const categorized = (() => {
      const newNotifs: DeltaNotificationResult[] = [];
      const removedNotifs: DeltaNotificationResult[] = [];
      const changedNotifs: DeltaNotificationResult[] = [];
      const unchangedNotifs: DeltaNotificationResult[] = [];

      for (const r of results) {
        switch (r.changeType) {
          case 'new': newNotifs.push(r); break;
          case 'removed': removedNotifs.push(r); break;
          case 'changed': changedNotifs.push(r); break;
          case 'unchanged': unchangedNotifs.push(r); break;
        }
      }
      return { newNotifs, removedNotifs, changedNotifs, unchangedNotifs };
    })();

    // Sort unchanged so notifications with affected dependencies float to top
    const sortedUnchanged = [...categorized.unchangedNotifs].sort((a, b) => {
      const aCount = computeChangedDependencies(a.dependencies, targetFieldsMap)
        .reduce((sum, d) => sum + d.changeCount, 0);
      const bCount = computeChangedDependencies(b.dependencies, targetFieldsMap)
        .reduce((sum, d) => sum + d.changeCount, 0);
      return bCount - aCount;
    });

    const summary = {
      total: results.length,
      newCount: categorized.newNotifs.length,
      removedCount: categorized.removedNotifs.length,
      changedCount: categorized.changedNotifs.length,
      unchangedCount: categorized.unchangedNotifs.length,
    };

    const renderSection = (
      label: string,
      items: DeltaNotificationResult[],
      type: DeltaNotificationResult['changeType']
    ) => {
      if (items.length === 0) return null;
      return (
        <div className="space-y-4">
          <Heading level="h3" className="font-semibold">
            {label} ({items.length})
          </Heading>
          {items.map((r, i) => {
            const targetPrefix = r.newNotification?.targetPrefix || r.oldNotification?.targetPrefix;
            return (
              <NotificationCard
                key={i}
                result={r}
                type={type}
                index={i}
                targetFieldsMap={targetFieldsMap}
                fullResult={targetPrefix ? fullNotifMap[targetPrefix] : undefined}
              />
            );
          })}
        </div>
      );
    };

    return (
      <div className="space-y-6 mb-16">
        {/* Summary card */}
        <Card>
          <CardBody>
            <div className="font-medium flex flex-col gap-1">
              <div>{summary.total} total notifications</div>
              {summary.newCount > 0 && <div className="text-green-600">{summary.newCount} new</div>}
              {summary.removedCount > 0 && <div className="text-red-600">{summary.removedCount} removed</div>}
              {summary.changedCount > 0 && <div className="text-yellow-600">{summary.changedCount} changed</div>}
              {summary.unchangedCount > 0 && <div className="text-gray-500">{summary.unchangedCount} unchanged</div>}
            </div>
            <div className="text-sm mt-4">
              <p>
                Note: notifications with recipient type "Screener" are{' '}
                <span className="font-bold">not yet supported</span> by the review tool and will not
                appear in these results.
              </p>
            </div>
          </CardBody>
        </Card>

        {renderSection('New Notifications', categorized.newNotifs, 'new')}
        {renderSection('Removed Notifications', categorized.removedNotifs, 'removed')}
        {renderSection('Changed Notifications', categorized.changedNotifs, 'changed')}

        {categorized.unchangedNotifs.length > 0 && (
          <div className="space-y-4">
            <Heading level="h3" className="font-semibold">
              Unchanged Notifications ({categorized.unchangedNotifs.length})
            </Heading>
            {sortedUnchanged.map((r, i) => {
              const targetPrefix = r.newNotification?.targetPrefix || r.oldNotification?.targetPrefix;
              return (
                <NotificationCard
                  key={i}
                  result={r}
                  type="unchanged"
                  index={i}
                  targetFieldsMap={targetFieldsMap}
                  fullResult={targetPrefix ? fullNotifMap[targetPrefix] : undefined}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Heading level="h2" className="text-lg font-semibold mb-4">Notifications Analysis</Heading>
      <WorkflowProgressTracker steps={stepperSteps} />
      {renderContent()}
    </>
  );
}
